import { getToken } from './api.js';

/**
 * Server-Sent Events client built on fetch rather than EventSource.
 *
 * EventSource cannot send an Authorization header, and the alternative — putting
 * the JWT in the query string — leaks it into access logs and browser history.
 * Reading the stream through fetch keeps the token in the header where it
 * belongs, at the cost of parsing the wire format ourselves.
 */
export function createStream({ onEvent, onStatus }) {
  let abort = null;
  let stopped = false;
  let attempt = 0;
  let timer = null;

  const setStatus = (s) => onStatus?.(s);

  /** Splits the byte stream into SSE frames and dispatches each one. */
  async function pump(body) {
    const reader = body.pipeThrough(new TextDecoderStream()).getReader();
    let buffer = '';

    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;

      // Frames are separated by a blank line; keep any partial tail in buffer.
      let split;
      while ((split = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);

        let event = 'message';
        const dataLines = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith(':')) continue; // heartbeat comment
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
        }
        if (!dataLines.length) continue;

        try {
          onEvent?.(event, JSON.parse(dataLines.join('\n')));
        } catch {
          /* malformed frame — skip it rather than tearing down the stream */
        }
      }
    }
  }

  async function connect() {
    if (stopped) return;
    const token = getToken();
    if (!token) return setStatus('idle');

    abort = new AbortController();
    setStatus(attempt === 0 ? 'connecting' : 'reconnecting');

    try {
      const res = await fetch('/api/stream', {
        headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
        signal: abort.signal,
        cache: 'no-store',
      });

      if (res.status === 401 || res.status === 403) {
        stopped = true;
        return setStatus('unauthorized');
      }
      if (!res.ok || !res.body) throw new Error(`stream failed (${res.status})`);

      attempt = 0;
      setStatus('open');
      await pump(res.body);
      // A clean end still means we lost the stream — fall through and retry.
      throw new Error('stream closed');
    } catch (err) {
      if (stopped || err?.name === 'AbortError') return;
      setStatus('offline');
      // Exponential backoff, capped, so a server restart does not spin.
      const delay = Math.min(1000 * 2 ** attempt++, 15000);
      timer = setTimeout(connect, delay);
    }
  }

  connect();

  // Reconnect promptly when the machine or tab comes back to life.
  const wake = () => {
    if (stopped || abort?.signal.aborted === false) return;
    clearTimeout(timer);
    attempt = 0;
    connect();
  };
  window.addEventListener('online', wake);

  return function close() {
    stopped = true;
    clearTimeout(timer);
    abort?.abort();
    window.removeEventListener('online', wake);
    setStatus('idle');
  };
}
